import * as React from 'react';
import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AgentChatActions, useAgentChat } from '../../../components/agent_chat/useAgentChat';
import { withCurrentUser } from '../../../hooks/withCurrentUser';
import { withUserType } from '../../../components/withUserType';
import {
	cancelAgentTurn,
	createAgentConversation,
	exchangeAgentLogin,
	fetchAgentIdentity,
	fetchAgentModels,
	resolveAgentApproval,
	sendAgentMessage,
	setAgentConversationModel,
	setAgentThinkingLevel,
	downloadAgentFile,
	uploadAgentFile,
} from '../../../thunks/agent_harness_thunks';
import { cn } from '../../../utils/ClassNamesUtils';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { OscarRoleProvider, useOscarTheme } from '../../context/OscarRoleContext';
import {
	readStoredConversationId,
	sessionIdentityMatches,
	storeConversationId,
	storePanelOpenIntent,
	storeSessionIdentity,
} from '../../utils/OscarConversationStorage';
import { OscarChatSummary } from '../../types/AIAgentTypes';
import OscarConnectionState, { oscarComposerPlaceholder } from '../shared/OscarConnectionState';
import OscarComposer from '../oscar_panel/OscarComposer';
import OscarContextBar from '../oscar_panel/OscarContextBar';
import OscarPanelHeader from '../oscar_panel/OscarPanelHeader';
import { OSCAR_FULL_MEASURE } from '../../constants/AIAgentConstants';
import OscarChatList from './OscarChatList';
import OscarTranscript from './OscarTranscript';

interface OscarFullScreenPageProps {
	userInitial?: string;
	contextSummary?: React.ReactNode;
	contextToggleLabel?: string;
	className?: string;
	/** Injected by `withUserType` — `state.session.userType`. */
	userType?: string;
	/** Injected by `withCurrentUser` — `state.session.currentUser`. */
	currentUser?: { email?: string; nameGiven?: string };
	/** From `mapDispatchToProps` below; shape required by `useAgentChat`. */
	exchangeLogin?: AgentChatActions['exchangeLogin'];
	fetchModels?: AgentChatActions['fetchModels'];
	fetchIdentity?: AgentChatActions['fetchIdentity'];
	createConversation?: AgentChatActions['createConversation'];
	sendMessage?: AgentChatActions['sendMessage'];
	cancelTurn?: AgentChatActions['cancelTurn'];
	setConversationModel?: AgentChatActions['setConversationModel'];
	setThinkingLevel?: AgentChatActions['setThinkingLevel'];
	resolveApproval?: AgentChatActions['resolveApproval'];
	uploadFile?: AgentChatActions['uploadFile'];
	/** Not part of the chat hook — passed straight to the transcript's file cards. */
	downloadFile?: (url: string) => Promise<Blob>;
}

/**
 * Figma node 14998:125437 — Oscar at full width, driven by the real agent
 * harness through `useAgentChat`.
 *
 * Reached from the panel's expand action at `/{buyer|supplier}/agent`, the
 * same path the sidebar menu entries use.
 * Shrink and close both go back, so the user lands on the docked panel.
 *
 * The recent-chats rail is hidden, not empty. `ow-agent-harness-service` keeps
 * conversations in an in-memory Map on the session — there is no list endpoint
 * and no persistence at all, so a conversation dies with its session (8h TTL, a
 * deploy, or eviction at MAX_SESSIONS). There is no history to browse yet, and a
 * rail headed "RECENT CHATS" over nothing advertises a feature that does not
 * exist. `OscarChatList` stays wired to `chats`, so it comes back on its own the
 * day the harness can answer.
 */
const OscarFullScreenPageBase = ({
	userInitial,
	contextSummary,
	contextToggleLabel,
	className,
	userType,
	currentUser,
	...actionProps
}: OscarFullScreenPageProps) => {
	const theme = useOscarTheme();
	const userEmail = nullSafeGetOrElse('email', currentUser, '');
	// The dock gets a name from `App.tsx`; this is a route, so nobody hands it
	// one and the avatar fell back to its placeholder "?" the moment you
	// expanded. Same source `App.tsx` uses for the dock.
	const initial =
		userInitial || nullSafeGetOrElse('nameGiven', currentUser, '?').charAt(0).toUpperCase();
	const history = useHistory();
	const [draft, setDraft] = useState('');
	const [showLogs, setShowLogs] = useState(false);
	const [contextCollapsed, setContextCollapsed] = useState(true);

	const actions: AgentChatActions = useMemo(
		() => ({
			exchangeLogin: actionProps.exchangeLogin,
			fetchModels: actionProps.fetchModels,
			fetchIdentity: actionProps.fetchIdentity,
			createConversation: actionProps.createConversation,
			sendMessage: actionProps.sendMessage,
			cancelTurn: actionProps.cancelTurn,
			setConversationModel: actionProps.setConversationModel,
			setThinkingLevel: actionProps.setThinkingLevel,
			resolveApproval: actionProps.resolveApproval,
			uploadFile: actionProps.uploadFile,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);
	// Shares the dock's conversation, so expanding carries the chat across
	// instead of starting a new one.
	// Bound to the signed-in account: the harness cookie and sessionStorage both
	// outlive an OpenWrench sign-out, so an unbound id would hand the next user
	// the previous user's conversation.
	const [initialConversationId] = useState(() => readStoredConversationId(userEmail));
	const chat = useAgentChat(actions, {
		conversationId: initialConversationId,
		onConversationId: (conversationId) => storeConversationId(userEmail, conversationId),
		// A live harness cookie is not proof of whose it is: it outlives sign-out
		// and a workspace switch. Reuse only when the harness agrees on the person
		// and our own record agrees on the workspace.
		canReuseSession: (harnessEmail, harnessRole) =>
			harnessEmail === userEmail &&
			harnessRole === userType &&
			sessionIdentityMatches(userEmail, userType),
		onSessionEstablished: () => storeSessionIdentity(userEmail, userType),
	});

	/**
	 * Leaving deliberately — Shrink or Close, not the left navigation.
	 *
	 * The intent is recorded here rather than when the panel expanded: set on the
	 * way in it survived someone walking off through the sidebar instead, and the
	 * dock then opened Oscar over whatever page they had picked.
	 *
	 * Going back is gated on the marker the dock pushes, not on
	 * `window.history.length` — that only proves *some* previous entry exists, so
	 * a fullscreen URL opened from another site still sent the user out of the
	 * app, which is the thing the check was added to prevent.
	 */
	const leave = () => {
		storePanelOpenIntent();
		if (nullSafeGetOrElse('state.fromDock', history.location, false)) {
			history.goBack();
			return;
		}
		history.push(`/${userType}/dashboards`);
	};

	const handleSubmit = (message: string) => {
		if (chat.busy || chat.status !== 'ready') {
			return;
		}
		void chat.sendMessage(message);
		setDraft('');
	};

	// Nothing can populate this yet — see the note above.
	const chats: OscarChatSummary[] = [];
	const hasHistory = chats.length > 0;

	return (
		<div
			className={cn(
				// h-screen, not h-full: the antd Content this renders into has no
				// definite height, so h-full would collapse to 0. A fixed height also
				// keeps the transcript scrolling internally instead of growing the page.
				'flex h-screen w-full items-center border-0 border-l-2 border-solid px-xs pb-2.5 pt-0',
				theme.surface,
				theme.panelEdge,
				className
			)}
		>
			<div className="flex h-full min-w-0 flex-1 flex-col">
				<OscarPanelHeader
					framedMark
					onNewChat={chat.newConversation}
					onShrink={leave}
					onClose={leave}
				/>
				<div
					className={cn(
						'flex min-h-0 w-full flex-1 rounded border-2 border-solid bg-white',
						theme.panelCardBorder
					)}
				>
					{hasHistory ? <OscarChatList chats={chats} onNewChat={chat.newConversation} /> : null}
					{/* The rule divides the rail from the conversation; with no rail it
					    would just be a line down the inside of the card. */}
					<div
						className={cn(
							'flex min-h-0 min-w-0 flex-1 flex-col',
							hasHistory && 'border-0 border-l border-solid border-grey-5'
						)}
					>
						<OscarTranscript
							measure={OSCAR_FULL_MEASURE}
							entries={chat.entries}
							busy={chat.busy}
							turnStartedAt={chat.turnStartedAt}
							userInitial={initial}
							onResolveApproval={chat.resolveApproval}
							unsentMessages={chat.unsentMessages}
							// While the connection strip is showing its own Try again, do not
							// offer a second one — they run the same action.
							onDownloadFile={actionProps.downloadFile}
							onRetrySend={chat.status === 'error' ? undefined : chat.retrySend}
						/>
						{contextSummary ? (
							<OscarContextBar
								summary={contextSummary}
								toggleLabel={contextToggleLabel}
								toggleChecked={showLogs}
								onToggleChange={setShowLogs}
								collapsed={contextCollapsed}
								onToggleCollapsed={() => setContextCollapsed(!contextCollapsed)}
								className="border-0 border-t border-solid border-grey-4"
							/>
						) : null}
						<OscarConnectionState
							status={chat.status}
							error={chat.errorInfo}
							onRetry={chat.retry}
						/>
						<OscarComposer
							value={draft}
							onChange={setDraft}
							onSubmit={handleSubmit}
							placeholder={oscarComposerPlaceholder(chat.status)}
							disabled={chat.status !== 'ready'}
							sending={chat.busy}
							onCancel={chat.stopTurn}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

const mapDispatchToProps = (dispatch: any, ownProps: { userType: string }) => ({
	exchangeLogin: () => dispatch(exchangeAgentLogin(ownProps.userType)),
	fetchModels: () => dispatch(fetchAgentModels()),
	fetchIdentity: () => dispatch(fetchAgentIdentity()),
	createConversation: (model?: string, thinkingLevel?: string) =>
		dispatch(createAgentConversation(model, thinkingLevel)),
	sendMessage: (conversationId: string, messageId: string, text: string) =>
		dispatch(sendAgentMessage(conversationId, messageId, text)),
	cancelTurn: (conversationId: string) => dispatch(cancelAgentTurn(conversationId)),
	setConversationModel: (conversationId: string, model: string) =>
		dispatch(setAgentConversationModel(conversationId, model)),
	setThinkingLevel: (conversationId: string, thinkingLevel: string) =>
		dispatch(setAgentThinkingLevel(conversationId, thinkingLevel)),
	resolveApproval: (approvalId: string, approved: boolean) =>
		dispatch(resolveAgentApproval(approvalId, approved)),
	uploadFile: (file: File) => dispatch(uploadAgentFile(file)),
	downloadFile: (url: string) => dispatch(downloadAgentFile(url, ownProps.userType)),
});

/**
 * The provider wraps the component that reads the theme, rather than being
 * rendered by it.
 *
 * `useOscarTheme()` inside `OscarFullScreenPageBase` resolved against the
 * default context — its own provider was further down its own tree — so it fell
 * through to `CURRENT_ENV_ROLE_TYPE`, which defaults to SUPPLIER. Fullscreen
 * showed cyan to a buyer whenever the build role and the session role differed.
 * This is the same trap the role context's own doc comment warns about.
 */
const OscarFullScreenPageWithRole = (props: OscarFullScreenPageProps) => (
	<OscarRoleProvider userType={props.userType}>
		<OscarFullScreenPageBase {...props} />
	</OscarRoleProvider>
);

export const OscarFullScreenPage = withUserType(
	withCurrentUser(connect(null, mapDispatchToProps)(OscarFullScreenPageWithRole))
);

export default OscarFullScreenPage;
